// Turn a failed mutation (Apollo/GraphQL error) into a readable message.
// genesis surfaces *validation* errors cleanly via ValidateXResult unions;
// unexpected server exceptions (e.g. a unique-name violation that isn't
// modelled as a Validate result) bubble up as opaque GraphQL errors - this
// maps the one a user can act on. User-facing copy is never hardcoded here:
// callers pass the i18n-resolved duplicate string, and validation results their
// fallback; the generic sentence comes from userErrorMessage.

import { CombinedGraphQLErrors } from "@apollo/client/errors";
import type { IntlShape } from "react-intl";
import { LocalizedError, localizedMessageOf } from "./localized-error";
import { userErrorMessage } from "./user-error";

export type ValidationIssueLike = {
  field?: string | null;
  error?: string | null;
  message?: string | null;
};

type ValidationResultLike = {
  __typename?: string | null;
  issues?: readonly ValidationIssueLike[] | null;
};

type GraphQlErrorLike = {
  message?: string | null;
  extensions?: Readonly<Record<string, unknown>> | null;
};

function graphQlErrors(e: unknown): readonly GraphQlErrorLike[] {
  if (CombinedGraphQLErrors.is(e)) return e.errors;
  if (e && typeof e === "object") {
    // Apollo Client 3 compatibility for errors crossing an older integration
    // boundary. Apollo Client 4 itself uses CombinedGraphQLErrors above.
    const errors = (e as { graphQLErrors?: readonly GraphQlErrorLike[] }).graphQLErrors;
    if (Array.isArray(errors)) return errors;
  }
  return [];
}

/**
 * genesis's classification for {@code InvalidIdentifierException} — an id that is not a UUID at all
 * (DefaultDataFetcherExceptionResolver). Unique to that exception, so it identifies it exactly.
 */
const INVALID_IDENTIFIER = "INVALID_IDENTIFIER";

/**
 * genesis's literal for {@code NoSuchEntityException}. Matched on the MESSAGE because the resolver
 * hands that exception to the persistence branch, where it shares the {@code PERSISTENCE}
 * classification with a real database fault — so classification alone cannot tell "this row does not
 * exist" from "the database is down", and only the literal can. Same mechanism, and the same reason,
 * as `isPermissionRefusal` in pages/company/company-admin-error.ts.
 */
const NO_SUCH_ENTITY = "no such entity";

/**
 * True when genesis's answer is "that is not a thing I have", rather than "something failed".
 *
 * A read whose id came out of a URL — spark drives `/c/:id` and `/p/:id` straight from the address
 * bar — meets this on every mistyped or truncated link, which is an ordinary event and not an
 * incident. Callers use it to keep such a read out of `userErrorMessage`: a support code on a typo
 * is noise that teaches people to ignore support codes (@adr SPARK-0010-no-raw-server-errors-in-ui@1 spends them on failures worth
 * reporting).
 *
 * It does NOT mean the entity is absent from the database — genesis answers an unreadable row and an
 * unknown id alike, deliberately — so a caller may only render "not found or not visible to you",
 * never "does not exist".
 */
export function isMissingEntityError(error: unknown): boolean {
  return graphQlErrors(error).some(
    (e) =>
      e.extensions?.["classification"] === INVALID_IDENTIFIER ||
      (e.message ?? "").trim().toLowerCase() === NO_SUCH_ENTITY,
  );
}

export function graphQlErrorExtensions(
  error: unknown,
  code: string,
): Readonly<Record<string, unknown>> | null {
  for (const graphQlError of graphQlErrors(error)) {
    if (graphQlError.extensions?.["code"] === code) {
      return graphQlError.extensions;
    }
  }
  return null;
}

function rawMessage(e: unknown): string {
  const errors = graphQlErrors(e);
  if (errors.length > 0) {
    return errors.map((error) => error.message ?? "").join(" · ");
  }
  if (e && typeof e === "object") {
    if (e instanceof Error) return e.message;
    const m = (e as { message?: string }).message;
    if (typeof m === "string") return m;
  }
  return typeof e === "string" ? e : "";
}

function isValidationResult(result: ValidationResultLike | null | undefined): boolean {
  return result?.__typename?.startsWith("Validate") ?? false;
}

/**
 * Localized copy for an issue genesis sent no `message` for, or null when the caller has none for
 * this field/error pair.
 *
 * The seam exists because THIS module cannot write the copy: it has no `intl` and no idea which
 * fields the operation has. Returning null is the honest answer and lands the caller on its own
 * generic fallback.
 */
export type ValidationIssueCopy = (issue: ValidationIssueLike) => string | null;

/**
 * A user-facing message for a `Validate…Result` payload, or `fallback` when there is nothing a user
 * could act on.
 *
 * `issue.message` is genesis's product copy and renders as-is — the single exception @adr SPARK-0010-no-raw-server-errors-in-ui@1
 * carves out, because those strings are written for users rather than being an error's internals.
 *
 * WHAT DOES NOT RENDER IS `issue.error` AND `issue.field`. Those used to be the fallback chain, so
 * an issue of `{ field: "name", error: "IS_MANDATORY", message: null }` put the literal token
 * "IS_MANDATORY" on screen: an enum from genesis's schema, unlocalized, shown as user copy — which
 * is exactly what @adr SPARK-0010-no-raw-server-errors-in-ui@1 exists to stop. `resolveIssue` is how a caller turns that pair into
 * copy it has written; an unresolved issue contributes nothing, and a payload of nothing but
 * unresolved issues comes back as `fallback`.
 */
export function validationResultMessage(
  result: ValidationResultLike | null | undefined,
  fallback: string,
  resolveIssue?: ValidationIssueCopy,
): string {
  if (!isValidationResult(result)) return fallback;

  const messages =
    result?.issues
      ?.map((issue) => issue.message?.trim() || resolveIssue?.(issue)?.trim() || null)
      .filter((message): message is string => Boolean(message)) ?? [];

  return messages.length > 0 ? messages.join("\n") : fallback;
}

export function throwForValidationResult(
  result: ValidationResultLike | null | undefined,
  fallback: string,
): never {
  throw new LocalizedError(validationResultMessage(result, fallback));
}

// Rejects when a mutation resolved with a Validate* union member, checked
// generically across the result's root fields. Genesis resolves such a
// mutation successfully, so without this the write silently does nothing and
// the user is told nothing.
export function assertNoValidationRejection(result: unknown, fallback: string): void {
  const data = (result as { data?: Record<string, unknown> | null } | null | undefined)?.data;
  for (const payload of Object.values(data ?? {})) {
    const candidate = payload as ValidationResultLike | null;
    if (isValidationResult(candidate)) throwForValidationResult(candidate, fallback);
  }
}

function looksLikeDuplicate(lower: string): boolean {
  return (
    lower.includes("duplicate") ||
    lower.includes("unique") ||
    lower.includes("already exists") ||
    lower.includes("bereits vorhanden")
  );
}

// LocalizedError first: a validation rejection thrown in the caller's try is already our copy (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
export function userFacingGraphqlError(intl: IntlShape, error: unknown, duplicateMessage?: string): string {
  const own = localizedMessageOf(error);
  if (own) return own;
  if (duplicateMessage && looksLikeDuplicate(rawMessage(error).toLowerCase())) return duplicateMessage;
  return userErrorMessage(intl, error);
}
